/**
 * عرض Live — the shared consultation queue.
 *
 * A port of `renderScheduleManagement` (doctor_dashboard.js:3369) together with
 * `_buildSecretaryContent` (:3547) and `_buildDoctorContent` (:3735). Both
 * machines open the same screen and see the same state: the secretary advances
 * the queue, the doctor sees whoever is currently in the room.
 *
 * The shared pointer lives on the server (SystemSetting `live_session_state`),
 * read and written through /api/sync/live-session. Both the date and the index
 * are shared, so when the secretary steps back a day the doctor's screen follows
 * — that is what the legacy poll did at :3510, and it is preserved here.
 *
 * The legacy poll ran every 2s from an interval re-created on every render and
 * cleared only on tab change. Here it is one `usePoll` that stops on unmount and
 * pauses while the tab is hidden.
 */

import { useCallback, useEffect, useMemo, useState } from 'react';
import { Button, Icon, Modal, Spinner } from '../../../components/common';
import { useAuth } from '../../../context/AuthContext';
import { useData } from '../../../context/DataContext';
import { useNotifications } from '../../../context/NotificationContext';
import { usePoll } from '../../../hooks/usePolling';
import { useDoctorBookings } from '../../../hooks/useBookings';
import { useAsync } from '../../../hooks/useAsync';
import { usePrescriptionActions, useAutocomplete } from '../../../hooks/usePrescriptions';
import { bookingApi, prescriptionApi, syncApi } from '../../../api/endpoints';
import { addDays, todayKey } from '../../../domain/dates';
import { AiAnalyzeButton } from '../../ai/AiAnalyzeButton';
import {
  ACTION_BUTTONS,
  COUNTER,
  LIVE_TOGGLE_DOCTOR,
  LIVE_TOGGLE_DOCTOR_SHADOW,
  LIVE_TOGGLE_SECRETARY,
  LIVE_TOGGLE_SECRETARY_SHADOW,
  PATIENT_HEADER,
  QUEUE_ROW,
} from '../calendar/legacyTheme';
import { PrescriptionEditor, emptyPrescription } from '../DoctorPrescriptions';
import { PrintMedicalRecordButton } from '../../../components/common/PrintMedicalRecordButton';
import { PrintPrescriptionButton } from '../../../components/common/PrintPrescriptionButton';
import { useBookingPrescription } from '../../../hooks/usePrescriptions';
import { usePermissions } from '../../../context/PermissionContext';
import { HospitalDoctorPanel } from './HospitalDoctorPanel';
import { AllergyBand } from '../../hospitalShared/AllergyBand';
import { MedicalFileView } from '../../hospitalShared/MedicalFileView';
import { patientsApi } from '../../../api/endpoints';
// المرحلة 1.4: the visit page, written from the room.
import { LiveEncounterPanel } from '../../encounter/LiveEncounterPanel';

/**
 * How often the two screens re-sync. The legacy value was 2000ms; 5s is gentler
 * on the server and still faster than a secretary can call the next patient.
 */
const LIVE_POLL_MS = 5000;

/** Turn a legacy inline-style string into a React style object. */
function toStyleObject(css) {
  return Object.fromEntries(
    css
      .split(';')
      .map((rule) => rule.trim())
      .filter(Boolean)
      .map((rule) => {
        const [prop, ...rest] = rule.split(':');
        const camel = prop.trim().replace(/-([a-z])/g, (_, c) => c.toUpperCase());
        return [camel, rest.join(':').trim()];
      }),
  );
}

const arabicFullDate = (dateKey) => {
  const date = new Date(`${dateKey}T00:00:00`);
  if (Number.isNaN(date.getTime())) return dateKey;
  return date.toLocaleDateString('ar-EG', {
    weekday: 'long',
    year: 'numeric',
    month: 'long',
    day: 'numeric',
  });
};

const arabicShortDate = (value, month = 'short') => {
  if (!value) return '';
  const date = new Date(value);
  if (Number.isNaN(date.getTime())) return String(value);
  return date.toLocaleDateString('ar-EG', { year: 'numeric', month, day: 'numeric' });
};

const patientLabel = (booking) =>
  booking?.bookedBy === 'admin'
    ? 'ADMIN'
    : booking?.patientName || booking?.manualPatientName || 'مريض';

/* ========================================================================== */

export function LiveView() {
  const { doctorId, doctorProfile } = useAuth();
  const { patients } = useData();
  const { success, error: toastError } = useNotifications();

  // A hospital doctor has a secretary of their own now — a separate person
  // with their own account (features/secretary). The shared-screen secretary
  // mode belongs to the independent doctor's single-login practice, so for a
  // hospital doctor it is removed and the doctor's view is the only one.
  const { institutionId, loading: permsLoading } = usePermissions();
  const inHospital = Boolean(institutionId);

  const [mode, setMode] = useState('secretary');
  useEffect(() => {
    if (inHospital) setMode('doctor');
  }, [inHospital]);
  const [dateKey, setDateKey] = useState(todayKey());
  const [liveIndex, setLiveIndex] = useState(-1);
  const [busy, setBusy] = useState(false);

  /* --- the day's queue ---------------------------------------------------- */

  const {
    bookings: allBookings,
    loading,
    setData,
    reload,
  } = useDoctorBookings(doctorId, { dateKey, intervalMs: LIVE_POLL_MS });

  // Cancelled bookings leave the queue entirely — the legacy filter at :3550.
  const queue = useMemo(
    () => allBookings.filter((b) => b.status !== 'cancelled'),
    [allBookings],
  );

  /* --- the shared pointer -------------------------------------------------- */

  /** Adopt the server's date and index. -1 means nobody is in the room yet. */
  const applySession = useCallback(
    (payload) => {
      const state = payload?.[String(doctorId)] || payload?.[doctorId];
      if (!state) return;
      if (state.liveDate) setDateKey(state.liveDate);
      if (state.liveIdx !== undefined && state.liveIdx !== null) {
        setLiveIndex(Number(state.liveIdx));
      }
    },
    [doctorId],
  );

  const session = useAsync(() => syncApi.getLiveSession(doctorId), [doctorId], {
    enabled: Boolean(doctorId),
  });

  useEffect(() => {
    applySession(session.data);
  }, [session.data, applySession]);

  usePoll(() => syncApi.getLiveSession(doctorId), doctorId ? LIVE_POLL_MS : null, {
    enabled: Boolean(doctorId),
    immediate: false,
    onData: applySession,
  });

  const publish = useCallback(
    (nextDate, nextIndex) =>
      syncApi.setLiveSession({ doctorId, liveDate: nextDate, liveIdx: nextIndex }),
    [doctorId],
  );

  /**
   * Move the pointer.
   *
   * Calling a patient marks their booking `in_progress` first, so the status is
   * already correct if the pointer sync then fails — the reverse order would
   * leave both screens pointing at someone the server never marked as being in
   * the room.
   */
  const moveTo = useCallback(
    async (nextIndex) => {
      if (nextIndex === liveIndex || busy) return;
      setBusy(true);
      try {
        if (nextIndex >= 0 && nextIndex < queue.length) {
          const target = queue[nextIndex];
          await bookingApi.updateStatus(target.id, 'in_progress');
          setData(
            allBookings.map((b) =>
              String(b.id) === String(target.id) ? { ...b, status: 'in_progress' } : b,
            ),
          );
        }
        await publish(dateKey, nextIndex);
        setLiveIndex(nextIndex);
      } catch (err) {
        toastError(err.message || 'تعذر تحديث الطابور');
      } finally {
        setBusy(false);
      }
    },
    [liveIndex, busy, queue, allBookings, setData, publish, dateKey, toastError],
  );

  /** Changing the day resets the pointer and moves both screens together. */
  const goToDate = useCallback(
    (nextDate) => {
      setDateKey(nextDate);
      setLiveIndex(-1);
      publish(nextDate, -1).catch((err) =>
        console.warn('[Live] failed to sync the date:', err.message),
      );
    },
    [publish],
  );

  /* --- the doctor's three actions ------------------------------------------ */

  const [cvBooking, setCvBooking] = useState(null);
  const [editing, setEditing] = useState(null);
  // Bumped on every save, so the print button looks the visit's script up
  // again — it only fetched when the patient changed, and kept saying
  // «لا توجد روشتة بعد» right after the doctor had written one.
  const [savedCount, setSavedCount] = useState(0);

  const { save, saving } = usePrescriptionActions(doctorId);
  const autocomplete = useAutocomplete(doctorId);

  /** "تم الكشف": complete the visit, then advance to the next patient (:4030). */
  const completeExam = useCallback(
    async (booking) => {
      setBusy(true);
      try {
        await bookingApi.updateStatus(booking.id, 'completed');
        setData(
          allBookings.map((b) =>
            String(b.id) === String(booking.id) ? { ...b, status: 'completed' } : b,
          ),
        );
        const next = liveIndex + 1;
        await publish(dateKey, next);
        setLiveIndex(next);
        success('تم إنهاء الكشف');
        reload();
      } catch (err) {
        toastError(err.message || 'تعذر إنهاء الكشف');
      } finally {
        setBusy(false);
      }
    },
    [allBookings, setData, liveIndex, publish, dateKey, success, reload, toastError],
  );

  const savePrescription = async (prescription) => {
    try {
      await save({ ...prescription, doctorId });
      success('تم حفظ الروشتة');
      setEditing(null);
      setSavedCount((n) => n + 1);
      autocomplete.reload();
    } catch (err) {
      // المرحلة 1.2: the server holds it for the doctor's confirmation; the editor asks.
      if (err.body && err.body.code === 'ALLERGY_CONFIRM') throw err;
      toastError(err.message || 'تعذر حفظ الروشتة');
    }
  };

  const isToday = dateKey === todayKey();
  const current = liveIndex >= 0 && liveIndex < queue.length ? queue[liveIndex] : null;

  return (
    <div className="mx-auto max-w-3xl py-4">
      {/* ── Header ─────────────────────────────────────────────────────────── */}
      <div className="mb-8 flex flex-col items-center">
        <div className="mb-5 flex items-center gap-3">
          <span className="relative flex h-3 w-3">
            <span className="absolute inline-flex h-full w-full animate-ping rounded-full bg-emerald-400 opacity-75" />
            <span className="relative inline-flex h-3 w-3 rounded-full bg-emerald-500" />
          </span>
          <h2 className="text-2xl font-black text-white">عرض Live</h2>
        </div>

        {/* Date navigation. Mirrored for RTL: chevron-right steps back a day. */}
        <div className="relative z-10 mb-6 flex w-full items-center justify-center gap-3">
          <button
            type="button"
            onClick={() => goToDate(addDays(dateKey, -1))}
            title="عرض اليوم الماضي"
            className="group rounded-xl border border-white/5 bg-slate-800 p-2.5 text-slate-400 shadow-lg transition-colors hover:bg-slate-700 hover:text-white"
          >
            <Icon
              name="chevron-right"
              size={20}
              className="transition-transform group-hover:-translate-x-0.5"
            />
          </button>

          <div
            className="min-w-[200px] rounded-xl px-6 py-2.5 text-center text-sm font-bold text-white"
            style={{
              background:
                'linear-gradient(135deg,rgba(255,255,255,0.05),rgba(255,255,255,0.02))',
              border: '1px solid rgba(255,255,255,0.1)',
            }}
          >
            {arabicFullDate(dateKey)}
          </div>

          <button
            type="button"
            onClick={() => goToDate(addDays(dateKey, 1))}
            title="انتقال لليوم التالي"
            className="group rounded-xl border border-white/5 bg-slate-800 p-2.5 text-slate-400 shadow-lg transition-colors hover:bg-slate-700 hover:text-white"
          >
            <Icon
              name="chevron-left"
              size={20}
              className="transition-transform group-hover:translate-x-0.5"
            />
          </button>

          <button
            type="button"
            onClick={() => goToDate(todayKey())}
            disabled={isToday}
            title="العودة لليوم"
            className={`absolute start-0 rounded-xl border p-2.5 shadow-lg transition-colors ${
              isToday
                ? 'cursor-default border-white/5 bg-slate-800 text-slate-600'
                : 'border-cyan-500/30 bg-cyan-500/20 text-cyan-400 hover:bg-cyan-500/30'
            }`}
          >
            <Icon name="calendar" size={20} />
          </button>
        </div>

        {/* Mode toggle — the same screen on both machines. Not offered to a
            hospital doctor: their secretary signs in as themselves. */}
        {!inHospital && !permsLoading && (
        <div
          className="flex items-center gap-1 rounded-2xl p-1"
          style={{
            background: 'rgba(255,255,255,0.05)',
            border: '1px solid rgba(255,255,255,0.08)',
          }}
        >
          <ModeButton
            active={mode === 'secretary'}
            onClick={() => setMode('secretary')}
            icon="monitor-check"
            label="سكرتارية"
            activeStyle={{
              background: LIVE_TOGGLE_SECRETARY,
              color: 'white',
              boxShadow: LIVE_TOGGLE_SECRETARY_SHADOW,
            }}
          />
          <ModeButton
            active={mode === 'doctor'}
            onClick={() => setMode('doctor')}
            icon="stethoscope"
            label="دكتور"
            activeStyle={{
              background: LIVE_TOGGLE_DOCTOR,
              color: 'white',
              boxShadow: LIVE_TOGGLE_DOCTOR_SHADOW,
            }}
          />
        </div>
        )}
      </div>

      {/* ── Content ────────────────────────────────────────────────────────── */}
      {loading && queue.length === 0 ? (
        <Spinner label="جاري تحميل الطابور..." />
      ) : mode === 'secretary' && !inHospital ? (
        <SecretaryView queue={queue} liveIndex={liveIndex} busy={busy} onMoveTo={moveTo} />
      ) : (
        <DoctorView
          queue={queue}
          liveIndex={liveIndex}
          current={current}
          doctor={doctorProfile}
          busy={busy}
          onViewCV={() => setCvBooking(current)}
          onWritePrescription={() =>
            setEditing({
              ...emptyPrescription(doctorProfile),
              patientId: current.patientId || null,
              patientName: patientLabel(current),
              bookingId: current.id,
              clinicName: current.clinicName || '',
            })
          }
          onComplete={() => completeExam(current)}
          savedCount={savedCount}
          autocomplete={autocomplete}
        />
      )}

      {inHospital && <HospitalDoctorPanel current={current} />}

      {cvBooking &&
        (inHospital ? (
          <HospitalRecordModal booking={cvBooking} doctor={doctorProfile} onClose={() => setCvBooking(null)} />
        ) : (
          <MedicalCVModal booking={cvBooking} doctor={doctorProfile} onClose={() => setCvBooking(null)} />
        ))}

      {editing && (
        <PrescriptionEditor
          prescription={editing}
          patients={patients}
          autocomplete={autocomplete}
          saving={saving}
          onClose={() => setEditing(null)}
          onSave={savePrescription}
        />
      )}
    </div>
  );
}

function ModeButton({ active, onClick, icon, label, activeStyle }) {
  return (
    <button
      type="button"
      onClick={onClick}
      className="flex items-center gap-2 rounded-xl px-6 py-2.5 text-sm font-bold transition-all"
      style={active ? activeStyle : { color: '#94a3b8', background: 'transparent' }}
    >
      <Icon name={icon} size={16} />
      {label}
    </button>
  );
}

/* ==========================================================================
   Secretary — the queue, and the control that advances it
   ========================================================================== */

function SecretaryView({ queue, liveIndex, busy, onMoveTo }) {
  const total = queue.length;
  // "خلص" counts strictly the patients before the pointer; the one at the
  // pointer is in the room, not finished. The legacy comment at :3572 calls
  // this out because an earlier version double-counted them.
  const done = Math.max(0, liveIndex);
  const inProgress = liveIndex >= 0 && liveIndex < total ? 1 : 0;
  const remaining = Math.max(0, total - done - inProgress);

  if (total === 0) {
    return (
      <div className="glass-panel rounded-2xl border border-dashed border-white/20 p-12 text-center">
        <Icon name="calendar-off" size={56} className="mx-auto mb-4 text-slate-500" />
        <h3 className="mb-2 text-xl font-bold text-white">لا توجد حجوزات اليوم</h3>
        <p className="text-sm text-slate-400">لا يوجد مرضى مجدولون لهذا اليوم</p>
      </div>
    );
  }

  return (
    <>
      <div className="mb-6 flex items-center justify-center gap-4">
        <CounterTile value={done} label="خلص" tone={COUNTER.done} />
        <Divider />
        <CounterTile value={total} label="إجمالي" tone={COUNTER.total} />
        <Divider />
        <CounterTile value={remaining} label="باقي" tone={COUNTER.remaining} />
      </div>

      <div className="mb-6 flex items-center justify-center gap-3">
        <button
          type="button"
          disabled={busy || liveIndex < 0}
          onClick={() => onMoveTo(Math.max(-1, liveIndex - 1))}
          className="flex items-center gap-2 rounded-xl px-5 py-2.5 text-sm font-bold transition-all disabled:opacity-50"
          style={{
            background: 'rgba(255,255,255,0.06)',
            border: '1px solid rgba(255,255,255,0.1)',
            color: '#94a3b8',
          }}
        >
          <Icon
            name={busy ? 'loader-2' : 'chevron-right'}
            size={16}
            className={busy ? 'animate-spin' : ''}
          />
          تراجع
        </button>

        <button
          type="button"
          disabled={busy || liveIndex >= total - 1}
          onClick={() => onMoveTo(Math.min(total - 1, liveIndex + 1))}
          className="flex items-center gap-2 rounded-xl px-5 py-2.5 text-sm font-bold text-white transition-all disabled:opacity-50"
          style={{
            background: 'linear-gradient(135deg,#10b981,#059669)',
            border: '1px solid rgba(16,185,129,0.4)',
            boxShadow: '0 4px 15px rgba(16,185,129,0.3)',
          }}
        >
          التالي
          <Icon
            name={busy ? 'loader-2' : 'chevron-left'}
            size={16}
            className={busy ? 'animate-spin' : ''}
          />
        </button>
      </div>

      <div className="space-y-1">
        {queue.map((booking, index) => (
          <QueueRow key={booking.id} booking={booking} index={index} liveIndex={liveIndex} />
        ))}
      </div>
    </>
  );
}

function QueueRow({ booking, index, liveIndex }) {
  const isActive = index === liveIndex;
  const isPast = index < liveIndex;
  const isAdmin = booking.bookedBy === 'admin';

  let css = QUEUE_ROW.upcoming;
  if (isActive) css = QUEUE_ROW.active;
  else if (isPast) css = QUEUE_ROW.past;

  // An admin booking overrides the state colour entirely, keeping only the
  // dimming and the stronger glow that mark past and active.
  if (isAdmin) {
    css =
      'border:1.5px solid #ef4444; background:rgba(239,68,68,0.08); box-shadow:0 0 10px rgba(239,68,68,0.2);' +
      (isPast ? ' opacity:0.6;' : '') +
      (isActive ? ' box-shadow:0 0 20px rgba(239,68,68,0.5); border-width:2px;' : '');
  }

  const time = booking.time || '--';
  const meridiem = parseInt(String(time).split(':')[0], 10) >= 12 ? 'PM' : 'AM';
  const isFollowup =
    booking.bookingCategory === 'followup' || booking.bookingType === 'followup';
  const price = booking.price || booking.finalPrice || 0;
  const showCode = !booking.patientId && booking.manualPatientCode;

  return (
    <div
      className="mb-3 flex items-center gap-4 rounded-2xl p-4 transition-all"
      style={toStyleObject(css)}
    >
      <div className="w-14 shrink-0 text-center">
        <div className="text-sm font-black text-white" dir="ltr">
          {time}
        </div>
        <div className="text-[10px] text-slate-500">{meridiem}</div>
      </div>

      <div
        className="h-10 w-0.5 shrink-0 rounded-full"
        style={{ background: isActive ? '#10b981' : isPast ? '#334155' : '#1e293b' }}
      />

      <div className="min-w-0 flex-1">
        <div className="truncate text-base font-bold text-white">{patientLabel(booking)}</div>
        <div className="mt-0.5 flex flex-wrap items-center gap-2">
          <span className="text-xs text-slate-400">
            {isFollowup ? 'متابعة' : 'كشف'} · {booking.clinicName || ''}
          </span>
          {price > 0 && (
            <span className="text-[11px] font-bold text-emerald-400">{price} ج.م</span>
          )}
          {isAdmin ? (
            <code className="rounded border border-red-500/20 bg-red-500/10 px-1.5 py-px font-mono text-[9px] text-red-400">
              M-ABBASJR1
            </code>
          ) : (
            showCode && (
              <code className="rounded border border-purple-500/20 bg-purple-500/10 px-1.5 py-px font-mono text-[9px] text-purple-400">
                {booking.manualPatientCode}
              </code>
            )
          )}
        </div>
      </div>

      <div>
        {isActive && (
          <span
            className="rounded-full px-2 py-0.5 text-[10px] font-black text-white"
            style={{ background: 'linear-gradient(135deg,#10b981,#059669)' }}
          >
            جاري الكشف
          </span>
        )}
        {isPast && (
          <span className="rounded-full bg-slate-800 px-2 py-0.5 text-[10px] font-bold text-slate-500">
            خلص
          </span>
        )}
      </div>
    </div>
  );
}

/* ==========================================================================
   Doctor — who is in the room, and their history in this specialty
   ========================================================================== */

function DoctorView({
  queue,
  liveIndex,
  current,
  doctor,
  busy,
  onViewCV,
  onWritePrescription,
  onComplete,
  savedCount = 0,
  autocomplete = null,
}) {
  const total = queue.length;
  const done = Math.max(0, liveIndex);
  const inProgress = liveIndex >= 0 && liveIndex < total ? 1 : 0;
  const remaining = Math.max(0, total - done - inProgress);

  return (
    <>
      <div className="mb-6 flex items-center justify-center gap-4">
        <CounterTile value={done} label="خلص" tone={COUNTER.donePurple} />
        <Divider />
        <CounterTile value={remaining} label="باقي النهارده" tone={COUNTER.remaining} />
      </div>

      {!current ? (
        <div className="glass-panel rounded-2xl border border-dashed border-white/15 p-10 text-center">
          <Icon name="user-x" size={48} className="mx-auto mb-3 text-slate-500" />
          <p className="text-sm text-slate-400">في انتظار السكرتارية لتحديد المريض التالي</p>
        </div>
      ) : (
        <>
          <PatientCard booking={current} doctor={doctor} />

          {/* The AI analyser.
              Above the three consultation actions, not beside them: those are
              equals (record / prescription / done) and a fourth peer would read
              as one more of the same. This is the one you press first — it
              reads the whole file so the doctor does not have to.

              Renders nothing when the doctor has no AI grant, so an account
              without it sees the screen exactly as it was before. */}
          <div className="mt-4">
            <AiAnalyzeButton
              patientId={current.patientId}
              manualPatientCode={current.manualPatientCode}
              bookingId={current.id}
              patientName={patientLabel(current)}
              specialty={doctor?.category}
            />
          </div>

          {/* Print row. Both documents are reachable without leaving the live
              tab — a doctor mid-consultation should not have to navigate away
              and lose the queue to hand a patient their record. */}
          <div className="mt-4 flex flex-wrap gap-2">
            <PrintMedicalRecordButton
              patientId={current.patientId}
              patientName={patientLabel(current)}
              patientCode={current.manualPatientCode || ''}
              patientPhone={current.patientPhone || ''}
              doctor={doctor}
              variant="subtle"
              label="طباعة السجل الطبي"
              className="flex-1"
            />
            <LivePrescriptionPrint key={`${current.id}-${savedCount}`} booking={current} doctor={doctor} />
          </div>

          <div className="mt-2 flex gap-3">
            <ActionButton
              label="السجل الطبي"
              icon="history"
              gradient={ACTION_BUTTONS.medicalCv}
              iconClass="text-indigo-200"
              onClick={onViewCV}
            />
            <ActionButton
              label="كتابة روشتة"
              icon="file-text"
              gradient={ACTION_BUTTONS.prescription}
              iconClass="text-cyan-200"
              onClick={onWritePrescription}
            />
            <ActionButton
              label="تم الكشف"
              icon={busy ? 'loader-2' : 'check-circle-2'}
              iconClass={busy ? 'animate-spin text-emerald-200' : 'text-emerald-200'}
              gradient={ACTION_BUTTONS.complete}
              onClick={onComplete}
              disabled={busy}
            />
          </div>

          {/* المرحلة 1.4: the visit's note and readings, under the three
              actions. Nothing renders where the Super Admin closed the
              visit page, so the screen stays what it was. */}
          <LiveEncounterPanel booking={current} autocomplete={autocomplete} />
        </>
      )}
    </>
  );
}

/**
 * The card for the patient in the room.
 *
 * The history section shows only prescriptions written by doctors in the same
 * specialty — a cardiologist does not need last month's dermatology script on
 * screen while examining. The full record is one button away.
 */
function PatientCard({ booking, doctor }) {
  const { categories, doctors } = useData();
  const isAdmin = booking.bookedBy === 'admin';
  const name = patientLabel(booking);

  const { prescriptions } = usePatientHistory(booking);

  const sameSpecialty = useMemo(() => {
    if (!doctor?.category) return prescriptions;
    return prescriptions.filter((p) => {
      const author = doctors.find((d) => String(d.id) === String(p.doctorId));
      return author?.category === doctor.category;
    });
  }, [prescriptions, doctors, doctor]);

  const specialtyName =
    categories.find((c) => c.id === doctor?.category || c.name === doctor?.category)?.name ||
    doctor?.category ||
    'نفس التخصص';

  const isFollowup =
    booking.bookingCategory === 'followup' || booking.bookingType === 'followup';
  const price = booking.price || booking.finalPrice || 0;
  const showCode = !booking.patientId && booking.manualPatientCode;

  return (
    <div
      className="glass-panel mb-4 overflow-hidden rounded-2xl"
      style={{ border: '1px solid rgba(255,255,255,0.07)' }}
    >
      {/* Header */}
      <div
        className="flex items-center gap-4 p-5"
        style={{
          background: isAdmin ? PATIENT_HEADER.admin : PATIENT_HEADER.normal,
          ...(isAdmin
            ? {
                boxShadow: '0 0 15px rgba(220,38,38,0.3)',
                borderBottom: '1px solid rgba(239,68,68,0.2)',
              }
            : {}),
        }}
      >
        <div
          className={`flex h-14 w-14 shrink-0 items-center justify-center rounded-2xl text-2xl font-black ${
            isAdmin ? 'text-red-300' : 'text-purple-300'
          }`}
          style={{
            background: isAdmin ? 'rgba(239,68,68,0.2)' : 'rgba(139,92,246,0.2)',
            border: `1px solid ${isAdmin ? 'rgba(239,68,68,0.3)' : 'rgba(139,92,246,0.3)'}`,
          }}
        >
          {isAdmin ? 'A' : name.charAt(0)}
        </div>

        <div className="min-w-0 flex-1">
          <div className="truncate text-lg font-black text-white">{name}</div>
          <div className="mt-1 flex flex-wrap items-center gap-2">
            <span className="text-xs text-slate-400">{isFollowup ? 'متابعة' : 'كشف'}</span>
            <span className="text-slate-600">·</span>
            <span className="text-xs text-slate-400" dir="ltr">
              {booking.time || ''}
            </span>
            {price > 0 && (
              <span className="text-xs font-bold text-emerald-400">{price} ج.م</span>
            )}
            {isAdmin ? (
              <code className="rounded border border-red-500/20 bg-red-500/10 px-1.5 py-0.5 font-mono text-[10px] text-red-400">
                M-ABBASJR1
              </code>
            ) : (
              showCode && (
                <code className="rounded border border-purple-500/20 bg-purple-500/10 px-1.5 py-0.5 font-mono text-[10px] text-purple-400">
                  {booking.manualPatientCode}
                </code>
              )
            )}
          </div>
        </div>
      </div>

      {/* المرحلة 1.2: the patient's allergies, red, before anything else is read. */}
      {(booking.patientId || booking.id) && (
        <div className="px-5 pt-4">
          <AllergyBand patientId={booking.patientId || null} bookingId={booking.id} />
        </div>
      )}

      {/* History in this specialty */}
      <div className="border-t border-white/5 p-5">
        <h4 className="mb-3 flex items-center gap-2 text-xs font-black text-slate-400">
          <Icon name="file-text" size={14} className="text-purple-400" />
          سجلات طبية في {specialtyName}
        </h4>

        {sameSpecialty.length === 0 ? (
          <p className="py-4 text-center text-sm text-slate-500">
            لا توجد سجلات سابقة في هذا التخصص
          </p>
        ) : (
          sameSpecialty.slice(0, 3).map((prescription) => (
            <div
              key={prescription.id}
              className="mb-2 rounded-xl p-3"
              style={{
                background: 'rgba(255,255,255,0.03)',
                border: '1px solid rgba(255,255,255,0.05)',
              }}
            >
              <div className="mb-1.5 flex items-center justify-between">
                <span className="text-xs text-slate-400">
                  {arabicShortDate(prescription.date)} ·{' '}
                  {prescription.PrescribingDoctor?.name || prescription.doctorName || 'دكتور'}
                </span>
              </div>

              {prescription.diagnosis && (
                <div className="mb-1 text-sm font-bold text-white">
                  🔍 {prescription.diagnosis}
                </div>
              )}

              {(prescription.medicines || []).length > 0 && (
                <div className="flex flex-wrap gap-1">
                  {prescription.medicines.map((medicine, index) => (
                    <span
                      key={`${medicine.name}-${index}`}
                      className="rounded-lg px-2 py-0.5 text-[11px] text-purple-300"
                      style={{
                        background: 'rgba(139,92,246,0.1)',
                        border: '1px solid rgba(139,92,246,0.15)',
                      }}
                    >
                      💊 {medicine.name}
                    </span>
                  ))}
                </div>
              )}
            </div>
          ))
        )}
      </div>
    </div>
  );
}

/**
 * Every prescription belonging to the patient in the room.
 *
 * A registered patient is looked up by id. A walk-in has no id and is found by
 * their `manualPatientCode` instead. The two are kept strictly separate so an
 * absent id can never match another absent id — the mismatch the legacy comment
 * at :3781 warns about, where two different manual patients shared a record.
 */
function usePatientHistory(booking) {
  const patientId = booking?.patientId;
  const manualCode = booking?.manualPatientCode;

  const state = useAsync(
    async () => {
      if (patientId) return prescriptionApi.listForPatient(patientId);
      if (manualCode) {
        const all = await prescriptionApi.list({ manualPatientCode: manualCode });
        return (all || []).filter((p) => p.manualPatientCode === manualCode);
      }
      return [];
    },
    [patientId, manualCode],
    { initialData: [], enabled: Boolean(patientId || manualCode) },
  );

  return { ...state, prescriptions: state.data || [] };
}

/**
 * Print the prescription written for the visit in progress.
 *
 * The live view holds a booking, not a prescription, so the visit's script is
 * looked up here. When none has been written yet the button says so rather than
 * printing an empty sheet — mid-consultation that is the normal state, not an
 * error.
 */
function LivePrescriptionPrint({ booking, doctor }) {
  const { prescription, loading, notFound } = useBookingPrescription(booking.id);

  if (loading) {
    return (
      <Button variant="subtle" icon="loader-2" disabled className="flex-1">
        جاري التحقق...
      </Button>
    );
  }

  if (notFound || !prescription) {
    return (
      <Button variant="subtle" icon="file-text" disabled className="flex-1" title="لم تُكتب روشتة لهذه الزيارة بعد">
        لا توجد روشتة بعد
      </Button>
    );
  }

  return (
    <PrintPrescriptionButton
      prescription={prescription}
      doctor={doctor}
      patientName={patientLabel(booking)}
      patientCode={booking.manualPatientCode || ''}
      variant="subtle"
      label="طباعة الروشتة"
      className="flex-1"
    />
  );
}

function ActionButton({ label, icon, gradient, iconClass, onClick, disabled = false }) {
  return (
    <button
      type="button"
      onClick={onClick}
      disabled={disabled}
      className="flex flex-1 items-center justify-center gap-2 rounded-xl py-3 text-sm font-bold text-white shadow-lg transition-all hover:scale-[1.02] disabled:opacity-60 disabled:hover:scale-100"
      style={{ background: gradient }}
    >
      <Icon name={icon} size={16} className={iconClass} />
      {label}
    </button>
  );
}

/* ==========================================================================
   السجل الطبي — the full record, every specialty, newest first
   ========================================================================== */

function MedicalCVModal({ booking, doctor, onClose }) {
  const { doctors } = useData();
  const { prescriptions, loading } = usePatientHistory(booking);

  const ordered = useMemo(
    () =>
      [...prescriptions].sort(
        (a, b) => new Date(b.createdAt || b.date) - new Date(a.createdAt || a.date),
      ),
    [prescriptions],
  );

  return (
    <Modal open onClose={onClose} title="السجل الطبي" icon="history" size="lg">
      <div className="-mt-2 mb-4 flex flex-wrap items-center justify-between gap-2">
        <p className="text-xs text-indigo-300">{patientLabel(booking)}</p>
        <PrintMedicalRecordButton
          patientId={booking.patientId}
          patientName={patientLabel(booking)}
          patientCode={booking.manualPatientCode || ''}
          patientPhone={booking.patientPhone || ''}
          prescriptions={prescriptions}
          doctor={doctor}
          variant="subtle"
          label="طباعة السجل كاملاً"
        />
      </div>

      {loading ? (
        <Spinner variant="inline" label="جاري تحميل السجل..." />
      ) : ordered.length === 0 ? (
        <div className="py-10 text-center">
          <div className="mx-auto mb-4 flex h-16 w-16 items-center justify-center rounded-full bg-white/5">
            <Icon name="file-x" size={32} className="text-slate-500" />
          </div>
          <p className="text-slate-400">لا توجد سجلات طبية سابقة لهذا المريض.</p>
        </div>
      ) : (
        <div className="space-y-4">
          {ordered.map((p) => {
            const author =
              p.PrescribingDoctor?.name ||
              doctors.find((d) => String(d.id) === String(p.doctorId))?.name ||
              'دكتور';
            return (
              <div
                key={p.id}
                className="rounded-2xl p-4"
                style={{
                  background: 'rgba(255,255,255,0.03)',
                  border: '1px solid rgba(255,255,255,0.05)',
                }}
              >
                <div className="mb-3 flex items-center justify-between border-b border-white/5 pb-3">
                  <div className="flex items-center gap-2">
                    <div className="flex h-8 w-8 items-center justify-center rounded-lg border border-cyan-500/20 bg-cyan-500/10 text-cyan-400">
                      <Icon name="calendar" size={16} />
                    </div>
                    <div>
                      <div className="text-sm font-bold text-white">
                        {arabicShortDate(p.date, 'long')}
                      </div>
                      <div className="text-[11px] text-slate-400">بواسطة {author}</div>
                    </div>
                  </div>

                  <PrintPrescriptionButton
                    prescription={p}
                    doctor={doctor}
                    patientName={patientLabel(booking)}
                    patientCode={booking.manualPatientCode || ''}
                    variant="subtle"
                    label="طباعة"
                    className="shrink-0"
                  />
                </div>

                {p.diagnosis && (
                  <RecordBlock label="التشخيص">
                    <div className="text-sm font-medium text-white">{p.diagnosis}</div>
                  </RecordBlock>
                )}

                {p.notes && (
                  <RecordBlock label="ملاحظات الطبيب">
                    <div className="rounded-lg border border-white/5 bg-white/5 p-2 text-sm text-slate-300">
                      {p.notes}
                    </div>
                  </RecordBlock>
                )}

                {(p.medicines || []).length > 0 && (
                  <RecordBlock label={`الأدوية الموصوفة (${p.medicines.length})`}>
                    <div className="flex flex-col gap-2">
                      {p.medicines.map((m, index) => (
                        <div
                          key={`${m.name}-${index}`}
                          className="flex items-center justify-between rounded-xl border border-purple-500/10 bg-purple-500/5 p-2 text-sm"
                        >
                          <div className="font-bold text-purple-200">💊 {m.name}</div>
                          <div className="text-[11px] text-purple-300/70">
                            {m.dosage || ''} {m.frequency ? `- ${m.frequency} مرات` : ''}
                          </div>
                        </div>
                      ))}
                    </div>
                  </RecordBlock>
                )}

                {(p.labTests || []).length > 0 && (
                  <RecordBlock label="التحاليل المطلوبة">
                    <div className="flex flex-col gap-2">
                      {p.labTests.map((lt, index) => (
                        <div
                          key={`${lt.name}-${index}`}
                          className="rounded-lg border border-blue-500/10 bg-blue-500/5 p-2"
                        >
                          <span className="mb-1 block text-xs font-bold text-blue-300">
                            🧪 {lt.name}
                          </span>
                          <ResultCount item={lt} />
                        </div>
                      ))}
                    </div>
                  </RecordBlock>
                )}

                {(p.xrays || []).length > 0 && (
                  <RecordBlock label="الأشعة المطلوبة">
                    <div className="flex flex-col gap-2">
                      {p.xrays.map((xr, index) => (
                        <div
                          key={`${xr.name}-${index}`}
                          className="rounded-lg border border-emerald-500/10 bg-emerald-500/5 p-2"
                        >
                          <span className="mb-1 block text-xs font-bold text-emerald-300">
                            🩻 {xr.name}
                          </span>
                          <ResultCount item={xr} />
                        </div>
                      ))}
                    </div>
                  </RecordBlock>
                )}
              </div>
            );
          })}
        </div>
      )}
    </Modal>
  );
}

/**
 * السجل الطبي for a hospital doctor: the hospital's unified file.
 *
 * The button opened the platform's prescription history, which for a patient
 * the hospital registered is empty — the file the desks, the lab, radiology
 * and the doctors fill lives in the hospital (MedicalFileView), and the doctor
 * had to scroll past the live queue to the panel below to reach it. The same
 * button now opens that file, for the booking in the room. A booking the desk
 * never linked to a hospital file falls back to the platform history.
 */
function HospitalRecordModal({ booking, doctor, onClose }) {
  const [state, setState] = useState({ loading: true, patient: null });
  const bookingId = booking?.id;

  useEffect(() => {
    let cancelled = false;
    setState({ loading: true, patient: null });
    patientsApi
      .byBooking(bookingId)
      .then((r) => !cancelled && setState({ loading: false, patient: r.patient || null }))
      .catch(() => !cancelled && setState({ loading: false, patient: null }));
    return () => {
      cancelled = true;
    };
  }, [bookingId]);

  if (!state.loading && !state.patient) {
    return <MedicalCVModal booking={booking} doctor={doctor} onClose={onClose} />;
  }

  return (
    <Modal open onClose={onClose} title={`الملف الطبي الموحد — ${state.patient?.fullName || patientLabel(booking)}`} icon="file-heart" size="xl">
      {state.loading ? (
        <Spinner variant="inline" label="جاري تحميل الملف الطبي الموحد..." />
      ) : (
        <MedicalFileView patientId={state.patient.id} bookingId={bookingId} />
      )}
    </Modal>
  );
}

function RecordBlock({ label, children }) {
  return (
    <div className="mb-3">
      <span className="mb-1 block text-xs text-slate-500">{label}</span>
      {children}
    </div>
  );
}

/** Uploaded results are opened from the prescriptions screen; here they are counted. */
function ResultCount({ item }) {
  const count = (item.results || item.resultFiles || []).length;
  if (!count) return <span className="text-[11px] text-slate-500">لم تُرفع النتيجة بعد</span>;
  return (
    <span className="text-[11px] text-slate-400">
      {count} {count === 1 ? 'ملف مرفوع' : 'ملفات مرفوعة'} — تُعرض من شاشة الروشتات
    </span>
  );
}

/* ========================================================================== */

function CounterTile({ value, label, tone }) {
  return (
    <div
      className="rounded-2xl px-5 py-3 text-center"
      style={{ background: tone.bg, border: `1px solid ${tone.border}` }}
    >
      <div className="text-2xl font-black" style={{ color: tone.text }}>
        {value}
      </div>
      <div className="mt-0.5 text-xs text-slate-400">{label}</div>
    </div>
  );
}

const Divider = () => <span className="text-2xl font-thin text-slate-600">/</span>;

export default LiveView;
